import React from 'react';
import {
  Box, Container, Typography, Grid, Divider, Stack,
} from '@mui/material';
import {
  LinkOutlined, ArticleOutlined, HubOutlined, TrendingUpOutlined, TuneOutlined,
  SupportAgentOutlined, VerifiedOutlined, InsightsOutlined, DescriptionOutlined,
  SecurityOutlined, GppGoodOutlined, ThumbUpOutlined, NorthEast,
} from '@mui/icons-material';
import { C, Section } from './theme';

const featureSections = [
  {
    category: 'Link quality',
    items: [
      {
        title: 'Real, high-traffic sites',
        desc: 'Every placement is on an established website with organic traffic — never link farms or PBNs.',
        icon: <LinkOutlined sx={{ color: '#6D28D9', fontSize: 20 }} />,
      },
      {
        title: 'DA/DR 50+ authority',
        desc: 'Domain Authority and Domain Rating are verified before every single placement.',
        icon: <VerifiedOutlined sx={{ color: '#059669', fontSize: 20 }} />,
      },
      {
        title: 'Niche-relevant context',
        desc: 'Links are placed in content that actually relates to your industry and keywords.',
        icon: <TuneOutlined sx={{ color: '#2563EB', fontSize: 20 }} />,
      },
      {
        title: 'Natural anchor mix',
        desc: 'Branded, partial-match, and generic anchors are blended to keep your profile natural.',
        icon: <ArticleOutlined sx={{ color: '#3B82F6', fontSize: 20 }} />,
      },
      {
        title: 'Ranking-focused strategy',
        desc: 'Every campaign is built around your target keywords, not just link count.',
        icon: <TrendingUpOutlined sx={{ color: '#6366F1', fontSize: 20 }} />,
      },
    ],
  },
  {
    category: 'Process & transparency',
    items: [
      {
        title: 'Manual outreach team',
        desc: 'Real people research and pitch every site — nothing is automated or bulk-purchased.',
        icon: <SupportAgentOutlined sx={{ color: '#D97706', fontSize: 20 }} />,
      },
      {
        title: 'Live order tracking',
        desc: 'Track every backlink from research to live placement inside your account dashboard.',
        icon: <InsightsOutlined sx={{ color: '#059669', fontSize: 20 }} />,
      },
      {
        title: 'Full placement report',
        desc: 'Get the live URL, anchor text, and authority metrics for every single link delivered.',
        icon: <DescriptionOutlined sx={{ color: '#6D28D9', fontSize: 20 }} />,
      },
      {
        title: 'No subscriptions lock-in',
        desc: 'Choose a one-time package or a recurring plan — cancel or scale anytime.',
        icon: <HubOutlined sx={{ color: '#2563EB', fontSize: 20 }} />,
      },
    ],
  },
  {
    category: 'Safety & guarantee',
    items: [
      {
        title: '100% white-hat',
        desc: 'Every technique follows Google\u2019s Webmaster Guidelines — no shortcuts, ever.',
        icon: <SecurityOutlined sx={{ color: '#059669', fontSize: 20 }} />,
      },
      {
        title: 'Penalty-risk free',
        desc: 'No PBNs, link schemes, or spammy directories — protecting your site long term.',
        icon: <GppGoodOutlined sx={{ color: '#6D28D9', fontSize: 20 }} />,
      },
      {
        title: 'Satisfaction guarantee',
        desc: 'If a placement is removed within the guarantee window, we replace it at no cost.',
        icon: <ThumbUpOutlined sx={{ color: '#2563EB', fontSize: 20 }} />,
      },
    ],
  },
];

const VpsFeatures = () => (
  <Section id="features" sx={{ background: '#FFFFFF', py: { xs: 8, md: 12 } }}>
    <Container maxWidth="lg">

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h2" sx={{
          fontWeight: 800,
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3.1rem' },
          color: '#0F172A',
          letterSpacing: '-0.03em',
          mb: 2,
        }}>
          Why marketers choose us for link building
        </Typography>
        <Typography sx={{
          color: '#475569',
          fontSize: { xs: '0.98rem', md: '1.05rem' },
          maxWidth: 820,
          mx: 'auto',
          lineHeight: 1.65,
        }}>
          Every package comes with the same standard: real websites, manual outreach, and full transparency —
          the fundamentals that keep your SEO growth safe and sustainable.
        </Typography>
      </Box>

      {/* Feature Sections with Dividers */}
      <Stack spacing={6}>
        {featureSections.map((sec, idx) => (
          <Box key={sec.category}>
            {/* MUI v6 Grid size */}
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start">

              {/* Left Column: Category Name */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.2rem', md: '1.35rem' },
                  color: '#0F172A',
                  letterSpacing: '-0.02em',
                }}>
                  {sec.category}
                </Typography>
              </Grid>

              {/* Right Column: Items Grid */}
              <Grid size={{ xs: 12, md: 9 }}>
                <Grid container spacing={{ xs: 3, md: 4 }}>
                  {sec.items.map((item) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.title}>
                      <Box sx={{ pr: 1 }}>
                        {/* Title Row with Icon & Arrow */}
                        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                              {item.icon}
                            </Box>
                            <Typography sx={{
                              fontWeight: 800,
                              fontSize: '0.95rem',
                              color: '#0F172A',
                              lineHeight: 1.25,
                            }}>
                              {item.title}
                            </Typography>
                          </Stack>
                          <NorthEast sx={{ fontSize: 14, color: '#94A3B8', flexShrink: 0 }} />
                        </Stack>

                        {/* Description */}
                        <Typography sx={{
                          color: '#475569',
                          fontSize: '0.86rem',
                          lineHeight: 1.6,
                        }}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

            </Grid>

            {/* Horizontal Divider Line */}
            {idx < featureSections.length - 1 && (
              <Divider sx={{ borderColor: '#E2E8F0', mt: 6 }} />
            )}
          </Box>
        ))}
      </Stack>

    </Container>
  </Section>
);

export default VpsFeatures;
